const READY = () => {
  if (document.readyState === "loading") {
    return new Promise((resolve) => {
      document.addEventListener("DOMContentLoaded", resolve, { once: true });
    });
  }

  return Promise.resolve();
};

function countActiveFilters(searchParams) {
  const keys = new Set();

  for (const [key, value] of searchParams.entries()) {
    if (!key.startsWith("filter.") || value === "") continue;
    keys.add(`${key}:${value}`);
  }

  ["category_filter", "variant_filter", "custom_filter"].forEach((key) => {
    const value = (searchParams.get(key) || "").trim();
    if (value) {
      keys.add(`${key}:${value}`);
    }
  });

  return keys.size;
}

function buildCollectionFilterUrl(value, param = "category_filter") {
  const url = new URL(window.location.href);

  if (!value || value === "all") {
    url.searchParams.delete(param);
  } else {
    url.searchParams.set(param, value);
  }

  return url;
}

function setDesktopFilterOpen(isOpen) {
  document.body.dataset.nativecosDesktopFilters = isOpen ? "open" : "closed";

  document.querySelectorAll("[data-nativecos-desktop-filter-toggle]").forEach((button) => {
    button.setAttribute("aria-expanded", String(isOpen));
    const copy = button.querySelector("[data-nativecos-filter-toggle-copy]");
    if (copy) {
      copy.textContent = isOpen ? "Hide Filters" : "Show Filters";
    }
  });

  document.querySelectorAll("[data-nativecos-desktop-sidebar]").forEach((panel) => {
    panel.hidden = !isOpen;
    panel.setAttribute("aria-hidden", String(!isOpen));
  });
}

function readCategoryTokens(card) {
  return String(card.getAttribute("data-nativecos-categories") || "")
    .split("|")
    .map((token) => token.trim())
    .filter(Boolean);
}

function readVariantTokens(card) {
  return String(card.getAttribute("data-nativecos-variant-filters") || "")
    .split("|")
    .map((token) => token.trim())
    .filter(Boolean);
}

function readProductUrl(card) {
  return String(card.getAttribute("data-product-url") || "").trim();
}

function readMetaCollectionProducts() {
  if (Array.isArray(window.meta?.products) && window.meta.products.length) {
    return window.meta.products;
  }

  if (Array.isArray(window.ShopifyAnalytics?.meta?.products) && window.ShopifyAnalytics.meta.products.length) {
    return window.ShopifyAnalytics.meta.products;
  }

  return [];
}

function nativecosHandleize(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " ")
    .replace(/\band\b/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildVariantTokensFromMetaProduct(product) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const tokens = new Set();

  variants.forEach((variant) => {
    const candidateValues = [
      variant?.public_title,
      variant?.title,
      variant?.name,
      variant?.option1,
      variant?.option2,
      variant?.option3,
    ].filter(Boolean);

    candidateValues.forEach((value) => {
      String(value)
        .split("/")
        .map((part) => nativecosHandleize(part))
        .filter(Boolean)
        .forEach((token) => {
          if (
            token &&
            token !== "default-title" &&
            token !== "default" &&
            token !== "classic" &&
            !/^\d+(-\d+)?(oz|ml|count|ct|pack)$/.test(token)
          ) {
            tokens.add(token);
          }
        });

      const fullToken = nativecosHandleize(value);
      if (
        fullToken &&
        fullToken !== "default-title" &&
        fullToken !== "default" &&
        fullToken !== "classic"
      ) {
        tokens.add(fullToken);
      }
    });
  });

  return [...tokens];
}

function enrichCollectionCardsFromMeta(grid) {
  if (!grid || grid.dataset.nativecosMetaFiltersReady === "true") return;

  const metaProducts = readMetaCollectionProducts();
  if (!metaProducts.length) return;

  const productMap = new Map(metaProducts.map((product) => [String(product.id), product]));

  grid.querySelectorAll(".nativecos-card-item[data-product-id]").forEach((card) => {
    if ((card.getAttribute("data-nativecos-variant-filters") || "").trim() !== "") return;

    const product = productMap.get(String(card.getAttribute("data-product-id") || ""));
    if (!product) return;

    const tokens = buildVariantTokensFromMetaProduct(product);
    if (tokens.length) {
      card.setAttribute("data-nativecos-variant-filters", tokens.join("|"));
    }
  });

  grid.dataset.nativecosMetaFiltersReady = "true";
}

async function enrichCollectionCardsFromRemote(grid) {
  if (!grid) return false;
  if (grid.dataset.nativecosRemoteFiltersReady === "true") return false;
  if (grid.dataset.nativecosRemoteFiltersLoading === "true") return false;

  const cardsToHydrate = [...grid.querySelectorAll(".nativecos-card-item[data-product-id]")]
    .filter((card) => readVariantTokens(card).length === 0 && readProductUrl(card) !== "");

  if (!cardsToHydrate.length) {
    grid.dataset.nativecosRemoteFiltersReady = "true";
    return false;
  }

  grid.dataset.nativecosRemoteFiltersLoading = "true";

  let didHydrate = false;

  try {
    await Promise.all(
      cardsToHydrate.map(async (card) => {
        try {
          const product = await fetchCollectionProductJson(readProductUrl(card));
          const tokens = buildVariantTokensFromMetaProduct(product);
          if (tokens.length) {
            card.setAttribute("data-nativecos-variant-filters", tokens.join("|"));
            didHydrate = true;
          }
        } catch (error) {
          console.error(error);
        }
      })
    );
  } finally {
    grid.dataset.nativecosRemoteFiltersLoading = "false";
    if (didHydrate || !cardsToHydrate.length) {
      grid.dataset.nativecosRemoteFiltersReady = "true";
    }
  }

  return didHydrate;
}

function getVisibleCards(grid) {
  return [...grid.querySelectorAll(".product-grid__item[data-nativecos-categories]")]
    .filter((card) => card.getAttribute("data-nativecos-hidden") !== "true");
}

function ensureEmptyState(grid) {
  const wrapper = grid.parentElement;
  if (!wrapper) return null;

  let emptyState = wrapper.querySelector("[data-nativecos-filter-empty]");
  if (emptyState) return emptyState;

  emptyState = document.createElement("div");
  emptyState.className = "nativecos-collection-filter-empty";
  emptyState.setAttribute("data-nativecos-filter-empty", "");
  emptyState.textContent = "No results";
  emptyState.hidden = true;
  wrapper.appendChild(emptyState);
  return emptyState;
}

const nativecosProductJsonCache = new Map();

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatMoney(cents) {
  const amount = Number(cents || 0) / 100;

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function stripHtml(value) {
  const fragment = document.createElement("div");
  fragment.innerHTML = String(value || "");
  return (fragment.textContent || fragment.innerText || "").replace(/\s+/g, " ").trim();
}

function appendWidthParam(url, width) {
  if (!url) return "";
  return url.includes("?") ? `${url}&width=${width}` : `${url}?width=${width}`;
}

function getCollectionOptionsModal() {
  return document.querySelector("[data-nativecos-options-modal]");
}

function getCollectionOptionsState() {
  const modal = getCollectionOptionsModal();
  if (!modal) return null;

  if (!modal.nativecosState) {
    modal.nativecosState = {
      product: null,
      selection: {},
      quantity: 1,
      selectedVariantId: null,
    };
  }

  return modal.nativecosState;
}

function setCollectionOptionsStatus(message, state = "success") {
  const modal = getCollectionOptionsModal();
  if (!modal) return;

  const status = modal.querySelector("[data-nativecos-modal-status]");
  if (!status) return;

  status.textContent = message || "";
  status.hidden = !message;
  if (message) {
    status.dataset.state = state;
  } else {
    status.removeAttribute("data-state");
  }
}

function uniqueVariantValues(variants, key) {
  return [...new Set((variants || []).map((variant) => variant?.[key]).filter(Boolean))];
}

function normalizeCollectionSelection(product, selection = {}) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const familyValues = uniqueVariantValues(variants, "option2");
  const sizeValues = uniqueVariantValues(variants, "option3");

  const normalized = {
    option1: selection.option1 || "",
    option2: familyValues.includes(selection.option2) ? selection.option2 : familyValues[0] || "",
    option3: sizeValues.includes(selection.option3) ? selection.option3 : sizeValues[0] || "",
  };

  let candidates = variants.filter((variant) => {
    if (normalized.option2 && variant.option2 !== normalized.option2) return false;
    if (normalized.option3 && variant.option3 !== normalized.option3) return false;
    return true;
  });

  if (!candidates.length) {
    candidates = variants;
  }

  if (!candidates.some((variant) => variant.option1 === normalized.option1)) {
    normalized.option1 = (candidates.find((variant) => variant.available) || candidates[0] || {}).option1 || "";
  }

  return normalized;
}

function findCollectionVariant(product, selection = {}) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const normalized = normalizeCollectionSelection(product, selection);
  const matching = variants.filter((variant) => {
    if (normalized.option1 && variant.option1 !== normalized.option1) return false;
    if (normalized.option2 && variant.option2 !== normalized.option2) return false;
    if (normalized.option3 && variant.option3 !== normalized.option3) return false;
    return true;
  });

  return matching.find((variant) => variant.available) || matching[0] || variants[0] || null;
}

function buildCollectionOptionButton(value, activeValue, attr) {
  const isActive = value === activeValue;

  return `
    <button
      type="button"
      class="nativecos-options-modal__choice${isActive ? " is-active" : ""}"
      data-nativecos-modal-choice="${attr}"
      data-nativecos-modal-value="${encodeURIComponent(value)}"
      aria-pressed="${isActive ? "true" : "false"}"
    >
      ${escapeHtml(value)}
    </button>
  `;
}

function buildCollectionScentTile(variant, activeScent) {
  const scentValue = variant?.option1 || variant?.title || "Option";
  const image =
    variant?.featured_image?.src ||
    variant?.featured_media?.preview_image?.src ||
    "";
  const isActive = scentValue === activeScent;
  const familyBadge =
    variant?.option2 && !/^classic$/i.test(variant.option2)
      ? `<span class="nativecos-options-modal__tile-badge">${escapeHtml(variant.option2)}</span>`
      : "";

  return `
    <button
      type="button"
      class="nativecos-options-modal__tile${isActive ? " is-active" : ""}"
      data-nativecos-modal-choice="option1"
      data-nativecos-modal-value="${encodeURIComponent(scentValue)}"
      aria-pressed="${isActive ? "true" : "false"}"
    >
      ${familyBadge}
      <span class="nativecos-options-modal__tile-media">
        ${image ? `<img src="${escapeHtml(appendWidthParam(image, 320))}" alt="${escapeHtml(scentValue)}" loading="lazy">` : ""}
      </span>
      <span class="nativecos-options-modal__tile-label">${escapeHtml(scentValue)}</span>
    </button>
  `;
}

function renderCollectionOptionsModal() {
  const modal = getCollectionOptionsModal();
  const state = getCollectionOptionsState();
  if (!modal || !state?.product) return;

  const product = state.product;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const familyValues = uniqueVariantValues(variants, "option2");
  const sizeValues = uniqueVariantValues(variants, "option3");
  const normalizedSelection = normalizeCollectionSelection(product, state.selection);
  const selectedVariant = findCollectionVariant(product, normalizedSelection);

  if (!selectedVariant) return;

  state.selection = {
    option1: selectedVariant.option1 || normalizedSelection.option1,
    option2: selectedVariant.option2 || normalizedSelection.option2,
    option3: selectedVariant.option3 || normalizedSelection.option3,
  };
  state.selectedVariantId = String(selectedVariant.id);

  const title = modal.querySelector("[data-nativecos-modal-title]");
  const description = modal.querySelector("[data-nativecos-modal-description]");
  const sizeBlock = modal.querySelector("[data-nativecos-modal-size-block]");
  const sizeGroup = modal.querySelector("[data-nativecos-modal-size-group]");
  const familyGroup = modal.querySelector("[data-nativecos-modal-family-group]");
  const selected = modal.querySelector("[data-nativecos-modal-selected]");
  const scentGrid = modal.querySelector("[data-nativecos-modal-scent-grid]");
  const quantityValue = modal.querySelector("[data-nativecos-modal-quantity-value]");
  const price = modal.querySelector("[data-nativecos-modal-price]");
  const submit = modal.querySelector("[data-nativecos-modal-submit]");

  if (title) {
    title.textContent = product.title || "Product";
  }

  if (description) {
    const descriptionText = stripHtml(product.description);
    description.textContent = descriptionText;
    description.hidden = !descriptionText;
  }

  if (sizeBlock && sizeGroup) {
    if (sizeValues.length > 1) {
      sizeBlock.hidden = false;
      sizeGroup.innerHTML = sizeValues
        .map((value) => buildCollectionOptionButton(value, state.selection.option3, "option3"))
        .join("");
    } else {
      sizeBlock.hidden = true;
      sizeGroup.innerHTML = "";
    }
  }

  if (familyGroup) {
    if (familyValues.length > 1) {
      familyGroup.hidden = false;
      familyGroup.innerHTML = familyValues
        .map((value) => buildCollectionOptionButton(value, state.selection.option2, "option2"))
        .join("");
    } else {
      familyGroup.hidden = true;
      familyGroup.innerHTML = "";
    }
  }

  const filteredVariants = variants.filter((variant) => {
    if (state.selection.option2 && variant.option2 !== state.selection.option2) return false;
    if (state.selection.option3 && variant.option3 !== state.selection.option3) return false;
    return true;
  });
  const scentVariants = [];
  const seenScents = new Set();

  filteredVariants.forEach((variant) => {
    const scentValue = variant.option1 || variant.title;
    if (!scentValue || seenScents.has(scentValue)) return;
    seenScents.add(scentValue);
    scentVariants.push(variant);
  });

  if (selected) {
    selected.textContent = state.selection.option1
      ? `Selected Scent: ${state.selection.option1}`
      : "";
    selected.hidden = !state.selection.option1;
  }

  if (scentGrid) {
    scentGrid.innerHTML = scentVariants
      .map((variant) => buildCollectionScentTile(variant, state.selection.option1))
      .join("");
  }

  if (quantityValue) {
    quantityValue.textContent = String(state.quantity || 1);
  }

  if (price) {
    price.textContent = formatMoney(selectedVariant.price);
  }

  if (submit) {
    const label = submit.querySelector("span");
    submit.disabled = !selectedVariant.available;
    submit.dataset.variantId = String(selectedVariant.id);
    if (label) {
      label.textContent = selectedVariant.available ? "Add to cart" : "Sold out";
    }
  }
}

async function fetchCollectionProductJson(productUrl) {
  const url = new URL(productUrl, window.location.origin);
  url.search = "";
  url.hash = "";

  const cacheKey = url.pathname;
  if (nativecosProductJsonCache.has(cacheKey)) {
    return nativecosProductJsonCache.get(cacheKey);
  }

  const response = await fetch(`${cacheKey}.js`, {
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load ${cacheKey}.js`);
  }

  const product = await response.json();
  nativecosProductJsonCache.set(cacheKey, product);
  return product;
}

async function fetchCollectionCartJson() {
  const response = await fetch("/cart.js", {
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Cart fetch failed");
  }

  return response.json();
}

function getCollectionCartSectionIds() {
  return Array.from(document.querySelectorAll("cart-items-component"))
    .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : null)
    .filter(Boolean);
}

function getCollectionCartAddPayload(items) {
  const payload = { items };
  const sectionIds = getCollectionCartSectionIds();

  if (sectionIds.length) {
    payload.sections = sectionIds.join(",");
    payload.sections_url = window.location.pathname;
  }

  return payload;
}

function extractCollectionCartItemCountFromSections(sections) {
  if (!sections || typeof sections !== "object") return null;

  const parser = new DOMParser();
  for (const sectionHtml of Object.values(sections)) {
    if (typeof sectionHtml !== "string") continue;

    const sectionDocument = parser.parseFromString(sectionHtml, "text/html");
    const count = Number(sectionDocument.querySelector('[ref="cartItemCount"]')?.textContent);
    if (Number.isFinite(count) && count >= 0) return count;
  }

  return null;
}

function getNativecosVisibleCartCount() {
  const countElement =
    document.querySelector("[data-nativecos-cart-count]:not([hidden])") ||
    document.querySelector("cart-icon [ref='cartBubbleCount']");
  const count = Number(countElement?.textContent || 0);
  return Number.isFinite(count) && count >= 0 ? count : null;
}

async function getCollectionCartFromAddPayload(addPayload, quantity) {
  const itemCount = extractCollectionCartItemCountFromSections(addPayload?.sections);
  if (itemCount !== null) {
    return { item_count: itemCount };
  }

  const currentVisibleCount = getNativecosVisibleCartCount();
  if (currentVisibleCount !== null) {
    return { item_count: currentVisibleCount + quantity };
  }

  return fetchCollectionCartJson();
}

function dispatchNativecosCartUpdate(cart, quantity, variantId, sourceId, sections = null) {
  const itemCount = Number.isFinite(Number(cart?.item_count)) ? Number(cart.item_count) : quantity;
  const data = {
    itemCount,
    source: "nativecos-collection",
    variantId,
  };

  if (sections) {
    data.sections = sections;
  }

  document.dispatchEvent(
    new CustomEvent("cart:update", {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId,
        data,
      },
    })
  );
}

function openCollectionOptionsModal() {
  const modal = getCollectionOptionsModal();
  if (!modal || typeof modal.showModal !== "function") return;

  if (!modal.open) {
    modal.showModal();
  }
}

function closeCollectionOptionsModal() {
  const modal = getCollectionOptionsModal();
  if (!modal || typeof modal.close !== "function") return;
  modal.close();
  setCollectionOptionsStatus("");
}

async function handleCollectionOptionsTrigger(trigger) {
  const state = getCollectionOptionsState();
  const productUrl = trigger.getAttribute("data-nativecos-product-url");
  const variantId = trigger.getAttribute("data-nativecos-variant-id");

  if (!state || !productUrl) return;

  setCollectionOptionsStatus("");
  state.quantity = 1;
  openCollectionOptionsModal();

  try {
    const product = await fetchCollectionProductJson(productUrl);
    const activeVariantFilter = nativecosHandleize(
      new URLSearchParams(window.location.search).get("variant_filter") || ""
    );
    const initialVariant =
      (product.variants || []).find((variant) => {
        if (!activeVariantFilter) return false;
        return (
          nativecosHandleize(variant.option1) === activeVariantFilter ||
          nativecosHandleize(variant.title) === activeVariantFilter
        );
      }) ||
      (product.variants || []).find((variant) => String(variant.id) === String(variantId)) ||
      (product.variants || []).find((variant) => variant.available) ||
      product.variants?.[0] ||
      null;

    state.product = product;
    state.selection = {
      option1: initialVariant?.option1 || "",
      option2: initialVariant?.option2 || "",
      option3: initialVariant?.option3 || "",
    };
    state.selectedVariantId = initialVariant ? String(initialVariant.id) : null;
    renderCollectionOptionsModal();
  } catch (error) {
    setCollectionOptionsStatus("Unable to load product options.", "error");
    console.error(error);
  }
}

async function addCollectionVariantToCart() {
  const modal = getCollectionOptionsModal();
  const state = getCollectionOptionsState();
  const submit = modal?.querySelector("[data-nativecos-modal-submit]");

  if (!modal || !state?.selectedVariantId || !submit) return;

  const quantity = Math.max(1, Number(state.quantity || 1));
  submit.disabled = true;
  setCollectionOptionsStatus("Adding to cart...");

  try {
    const addResponse = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(
        getCollectionCartAddPayload([
          {
            id: Number(state.selectedVariantId),
            quantity,
          },
        ])
      ),
    });

    if (!addResponse.ok) {
      throw new Error("Cart add failed");
    }

    const addPayload = await addResponse.json();
    const cart = await getCollectionCartFromAddPayload(addPayload, quantity);
    dispatchNativecosCartUpdate(cart, quantity, state.selectedVariantId, "nativecos-options-modal", addPayload.sections);

    setCollectionOptionsStatus("Added to cart.");
  } catch (error) {
    setCollectionOptionsStatus("Could not add this product to the cart.", "error");
    console.error(error);
  } finally {
    submit.disabled = false;
  }
}

async function addCollectionCardVariantToCart(form) {
  const variantInput = form.querySelector('input[name="id"]');
  const quantityInput = form.querySelector('input[name="quantity"]');
  const submitButton = form.querySelector('button[type="submit"]');
  const variantId = variantInput?.value;
  const quantity = Math.max(1, Number(quantityInput?.value || 1));
  if (!variantId || !submitButton) return;

  const originalLabel = submitButton.textContent;
  submitButton.disabled = true;
  submitButton.textContent = "Adding...";

  try {
    const addResponse = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(
        getCollectionCartAddPayload([
          {
            id: Number(variantId),
            quantity,
          },
        ])
      ),
    });

    if (!addResponse.ok) {
      throw new Error("Card cart add failed");
    }

    const addPayload = await addResponse.json();
    const cart = await getCollectionCartFromAddPayload(addPayload, quantity);
    dispatchNativecosCartUpdate(cart, quantity, variantId, "nativecos-product-card", addPayload.sections);
    submitButton.textContent = "Added";
  } catch (error) {
    submitButton.textContent = "Try again";
    console.error(error);
  } finally {
    window.setTimeout(() => {
      submitButton.disabled = false;
      submitButton.textContent = originalLabel;
    }, 1200);
  }
}

function applyCollectionCategoryFilter() {
  const shell = document.querySelector("[data-nativecos-collection-shell]");
  const grid = document.querySelector(".product-grid");
  if (!grid) return;

  enrichCollectionCardsFromMeta(grid);

  const params = new URLSearchParams(window.location.search);
  const activeCategory = (params.get("category_filter") || "all").trim().toLowerCase();
  const activeVariant = nativecosHandleize(params.get("variant_filter") || "");
  const activeCustom = nativecosHandleize(params.get("custom_filter") || "");
  const activeFilterCount = countActiveFilters(params);
  const countTargets = document.querySelectorAll("[data-nativecos-results-count]");
  const filterCountTargets = document.querySelectorAll("[data-nativecos-filter-count]");
  const tabs = document.querySelectorAll("[data-nativecos-category-tab]");
  const filterOptions = document.querySelectorAll("[data-nativecos-filter-option]");
  const clearButtons = document.querySelectorAll("[data-nativecos-clear-filter]");
  const cards = [...grid.querySelectorAll(".product-grid__item[data-nativecos-categories]")];
  const promos = [...grid.querySelectorAll("[data-nativecos-promo-card]")];
  const emptyState = ensureEmptyState(grid);
  const needsRemoteVariantHydration =
    Boolean(activeVariant || activeCustom) &&
    cards.some((card) => readVariantTokens(card).length === 0 && readProductUrl(card) !== "");

  if (needsRemoteVariantHydration) {
    enrichCollectionCardsFromRemote(grid).then((didHydrate) => {
      if (didHydrate) {
        applyCollectionCategoryFilter();
      }
    });
  }

  tabs.forEach((tab) => {
    const isActive = (tab.getAttribute("data-nativecos-category-tab") || "all") === activeCategory;
    tab.classList.toggle("is-active", isActive);
  });

  filterOptions.forEach((option) => {
    const isActive = (option.getAttribute("data-nativecos-filter-option") || "all") === activeCategory;
    option.classList.toggle("is-active", isActive);
    option.setAttribute("aria-current", isActive ? "page" : "false");
  });

  clearButtons.forEach((button) => {
    button.hidden = activeCategory === "all";
  });

  filterCountTargets.forEach((target) => {
    target.textContent = String(activeFilterCount);
  });

  if (!cards.length) {
    const defaultCount = Number(shell?.getAttribute("data-nativecos-default-count") || 0);
    countTargets.forEach((target) => {
      target.textContent = `${defaultCount} ${defaultCount === 1 ? "Result" : "Results"}`;
    });
    return;
  }

  cards.forEach((card) => {
    const categoryTokens = readCategoryTokens(card);
    const variantTokens = readVariantTokens(card);
    const matchesCategory = activeCategory === "all" || categoryTokens.includes(activeCategory);
    const matchesVariant =
      !activeVariant ||
      (needsRemoteVariantHydration && variantTokens.length === 0) ||
      variantTokens.includes(activeVariant) ||
      categoryTokens.includes(activeVariant);
    const matchesCustom =
      !activeCustom ||
      (needsRemoteVariantHydration && variantTokens.length === 0) ||
      categoryTokens.includes(activeCustom) ||
      variantTokens.includes(activeCustom);
    const matches = matchesCategory && matchesVariant && matchesCustom;
    card.setAttribute("data-nativecos-hidden", matches ? "false" : "true");
  });

  promos.forEach((promo) => {
    const shouldShowPromo = activeCategory === "all" && !activeVariant && !activeCustom;
    promo.setAttribute("data-nativecos-hidden", shouldShowPromo ? "false" : "true");
  });

  const visibleCount = getVisibleCards(grid).length;
  countTargets.forEach((target) => {
    target.textContent = `${visibleCount} ${visibleCount === 1 ? "Result" : "Results"}`;
  });

  if (emptyState) {
    emptyState.hidden = visibleCount !== 0;
  }
}

READY().then(() => {
  const isCollectionTemplate = Boolean(document.querySelector("main[data-template*='collection']"));
  const grid = isCollectionTemplate ? document.querySelector(".product-grid") : null;

  if (isCollectionTemplate) {
    setDesktopFilterOpen(false);
    applyCollectionCategoryFilter();
  }

  const optionsModal = getCollectionOptionsModal();
  if (optionsModal) {
    optionsModal.addEventListener("click", (event) => {
      if (event.target === optionsModal) {
        closeCollectionOptionsModal();
      }
    });
  }

  document.addEventListener("click", (event) => {
    const desktopToggle = event.target.closest("[data-nativecos-desktop-filter-toggle]");
    if (desktopToggle) {
      event.preventDefault();
      if (!isCollectionTemplate || window.innerWidth < 750) return;
      const isOpen = document.body.dataset.nativecosDesktopFilters === "open";
      setDesktopFilterOpen(!isOpen);
      return;
    }

    const clearButton = event.target.closest("[data-nativecos-clear-filter]");
    if (clearButton) {
      event.preventDefault();
      if (!isCollectionTemplate) return;
      window.history.replaceState({}, "", buildCollectionFilterUrl("all"));
      applyCollectionCategoryFilter();
      return;
    }

    const optionsTrigger = event.target.closest("[data-nativecos-options-trigger]");
    if (optionsTrigger && getCollectionOptionsModal()) {
      event.preventDefault();
      handleCollectionOptionsTrigger(optionsTrigger);
      return;
    }

    const filterLink = event.target.closest("[data-nativecos-category-tab], [data-nativecos-filter-option]");
    if (filterLink) {
      event.preventDefault();
      if (!isCollectionTemplate) return;
      const value =
        filterLink.getAttribute("data-nativecos-category-tab") ||
        filterLink.getAttribute("data-nativecos-filter-option") ||
        "all";

      window.history.replaceState({}, "", buildCollectionFilterUrl(value));
      applyCollectionCategoryFilter();
      return;
    }

    const optionsClose = event.target.closest("[data-nativecos-options-close]");
    if (optionsClose) {
      event.preventDefault();
      closeCollectionOptionsModal();
      return;
    }

    const quantityButton = event.target.closest("[data-nativecos-modal-quantity]");
    if (quantityButton) {
      event.preventDefault();
      const state = getCollectionOptionsState();
      if (!state) return;

      const direction = quantityButton.getAttribute("data-nativecos-modal-quantity");
      const nextQuantity =
        direction === "decrease"
          ? Math.max(1, Number(state.quantity || 1) - 1)
          : Math.min(99, Number(state.quantity || 1) + 1);

      state.quantity = nextQuantity;
      renderCollectionOptionsModal();
      return;
    }

    const choiceButton = event.target.closest("[data-nativecos-modal-choice]");
    if (choiceButton) {
      event.preventDefault();
      const state = getCollectionOptionsState();
      if (!state?.product) return;

      const key = choiceButton.getAttribute("data-nativecos-modal-choice");
      const value = decodeURIComponent(choiceButton.getAttribute("data-nativecos-modal-value") || "");
      if (!key) return;

      state.selection = {
        ...state.selection,
        [key]: value,
      };
      renderCollectionOptionsModal();
      return;
    }

    const addButton = event.target.closest("[data-nativecos-modal-submit]");
    if (addButton) {
      event.preventDefault();
      addCollectionVariantToCart();
    }
  });

  document.addEventListener("submit", (event) => {
    const purchaseForm = event.target.closest(".nativecos-product-card__purchase-form");
    if (!purchaseForm) return;
    event.preventDefault();
    addCollectionCardVariantToCart(purchaseForm);
  });

  if (isCollectionTemplate) {
    window.addEventListener("resize", () => {
      if (window.innerWidth < 750) {
        setDesktopFilterOpen(false);
      }
    });

    if (grid) {
      const observer = new MutationObserver(() => {
        applyCollectionCategoryFilter();
      });

      observer.observe(grid, {
        childList: true,
        subtree: false,
      });
    }
  }
});
